import { makeid } from 'utils/helpers/makeid';

import { ER_PRECAUTIONS_SCRIPT } from '../scripts/constants';
import { OUTCOME_LABELS } from '../engine/types';

import type { Outcome } from '../engine/types';
import type { TFormItem } from '../../types';

const OUTCOME_ORDER: Outcome[] = [
  'warm-transfer',
  'escalate-clinic',
  '911',
  'reading-recovered-closed',
  'do-not-escalate',
  'follow-runbook',
];

interface Step6Args {
  showEscalationMethod: boolean;
  isWarmTransfer: boolean;
  showErPrecautions: boolean;
  showRunbookAction: boolean;
  showOverrideReason: boolean;
  anySymptomYes: boolean;
}

export const buildStep6Config = ({
  showEscalationMethod,
  isWarmTransfer,
  showErPrecautions,
  showRunbookAction,
  showOverrideReason,
  anySymptomYes,
}: Step6Args): TFormItem[] => {
  const items: TFormItem[] = [];

  // Emergency Symptom Prompt — shown when any #5 symptom was answered Yes.
  if (anySymptomYes) {
    items.push({
      key: makeid(10),
      name: 'emergencyPromptInfo',
      type: 'info',
      html: (
        <div>
          <p style={{ fontWeight: 700 }}>Potential Emergency Symptoms</p>
          <p>
            Patient is reporting potential emergency symptoms. This requires a
            warm transfer to a Tellihealth nurse.
          </p>
        </div>
      ),
    });
  }

  items.push({
    key: makeid(10),
    name: 'outcome',
    type: 'radio',
    title: <p>#6 MA Intervention / Outcome</p>,
    description:
      'The recommended outcome is pre-selected. You may escalate further or redirect, but not downgrade. Any change requires a reason.',
    required: true,
    options: OUTCOME_ORDER.map((value) => ({
      key: makeid(10),
      label: OUTCOME_LABELS[value],
      value,
    })),
  });

  // Reason field only appears once the MA changes the pre-selected outcome.
  if (showOverrideReason) {
    items.push({
      key: makeid(10),
      name: 'overrideReason',
      type: 'textarea',
      title: <p>Reason for changing the recommended outcome</p>,
      required: true,
    });
  }

  if (showRunbookAction) {
    items.push({
      key: makeid(10),
      name: 'runbookAction',
      type: 'radio',
      title: <p>Runbook action</p>,
      required: true,
      options: [
        {
          key: makeid(10),
          label: 'Warm Transfer/Callback to TH Nurse',
          value: 'warm-transfer',
        },
        {
          key: makeid(10),
          label: 'Escalate to Clinic',
          value: 'escalate-clinic',
        },
        { key: makeid(10), label: '911', value: '911' },
        {
          key: makeid(10),
          label: 'Other',
          value: 'other',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              name: 'runbookActionOther',
              type: 'input',
              title: <p>Specify</p>,
              required: true,
            },
          ],
        },
      ],
    });
  }

  if (showEscalationMethod) {
    items.push({
      key: makeid(10),
      name: 'escalationMethod',
      type: 'radio',
      title: <p>Escalation Method</p>,
      required: true,
      options: [
        { key: makeid(10), label: 'Phone', value: 'phone' },
        { key: makeid(10), label: 'SMS', value: 'sms' },
        { key: makeid(10), label: 'Email', value: 'email' },
        { key: makeid(10), label: 'Create Case in EHR', value: 'ehr-case' },
        {
          key: makeid(10),
          label: 'Route to CCM Team (RPM+CCM patients)',
          value: 'ccm-team',
        },
      ],
    });
  }

  if (isWarmTransfer) {
    items.push({
      key: makeid(10),
      name: 'warmTransferConfirmed',
      type: 'checkbox',
      title: null,
      required: true,
      options: [
        {
          key: makeid(10),
          label: 'Successfully warm transferred patient to Tellihealth nurse',
          value: 'confirmed',
        },
      ],
    });
  }

  if (showErPrecautions) {
    items.push({
      key: makeid(10),
      name: 'erPrecautionsInfo',
      type: 'info',
      html: (
        <div>
          <p style={{ fontWeight: 600 }}>ER Precautions — read aloud:</p>
          <p>{ER_PRECAUTIONS_SCRIPT}</p>
        </div>
      ),
    });
  }

  return items;
};
